import React, { useEffect, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Heading, Text, SimpleGrid, Card, CardBody, Link } from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, ActiveEventStats } from '../services/api';

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const ActiveEventCard: React.FC<{ event: ActiveEventStats; onOpen: () => void }> = ({
  event,
  onOpen,
}) => (
  <Card
    bg="blue.700"
    color="white"
    cursor="pointer"
    role="link"
    tabIndex={0}
    data-testid="active-event-card"
    onClick={onOpen}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    }}
    _hover={{ bg: 'blue.800' }}
  >
    <CardBody>
      <Heading size="md" mb={1}>
        {event.title}
      </Heading>
      <Text fontSize="sm" color="blue.100">
        {formatDate(event.event_date)}
      </Text>
      {event.location && (
        <Text fontSize="sm" color="blue.100" mb={4}>
          {event.location}
        </Text>
      )}
      <SimpleGrid columns={3} spacing={2} mt={event.location ? 0 : 4}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            {event.total_invitees}
          </Text>
          <Text fontSize="xs" color="blue.100">
            Invités
          </Text>
        </Box>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            {event.response_rate}%
          </Text>
          <Text fontSize="xs" color="blue.100">
            Taux de réponse
          </Text>
        </Box>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            {event.accepted}
          </Text>
          <Text fontSize="xs" color="blue.100">
            Présents
          </Text>
        </Box>
      </SimpleGrid>
    </CardBody>
  </Card>
);

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState<ActiveEventStats[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getActiveEventStats()
      .then((data) => {
        if (!cancelled) setEvents(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <Heading mb={6} size={{ base: 'lg', md: 'xl' }}>
        Tableau de bord
      </Heading>

      <Box mb={8} data-testid="dashboard-user">
        <Text mb={1} color="gray.600" fontSize={{ base: 'md', md: 'lg' }}>
          Bienvenue, {user?.full_name || user?.first_name}!
        </Text>

        <Text fontSize="sm" color="gray.500">
          Rôle: {user?.role} | Email: {user?.email}
        </Text>
      </Box>

      {error && <Text color="gray.600">Données indisponibles</Text>}
      {!error && events === null && <Text color="gray.600">Chargement…</Text>}
      {events !== null && events.length === 0 && (
        <Box data-testid="no-active-events">
          <Text mb={2}>Aucun événement actif</Text>
          <Link as={RouterLink} to="/events" color="blue.600">
            Voir la liste des événements
          </Link>
        </Box>
      )}
      {events !== null && events.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 4, md: 6 }}>
          {events.map((event) => (
            <ActiveEventCard
              key={event.id}
              event={event}
              onOpen={() => navigate(`/events/${event.id}`)}
            />
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default DashboardPage;
